import React from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from './ui/badge';
import { 
  MapPin, 
  Clock, 
  Wrench, 
  Sparkles, 
  Truck, 
  MoreHorizontal,
  DollarSign,
  Zap
} from 'lucide-react';

const categoryIcons = {
  plumbing: { icon: Wrench, color: 'bg-blue-500' },
  cleaning: { icon: Sparkles, color: 'bg-emerald-500' },
  delivery: { icon: Truck, color: 'bg-amber-500' },
  others: { icon: MoreHorizontal, color: 'bg-purple-500' }
};

export const JobCard = ({ job, index = 0, onClick, showMapPin = false }) => {
  const { t } = useTranslation();
  
  const category = categoryIcons[job.category] || categoryIcons.others;
  const CategoryIcon = category.icon;
  const hasLocation = job.location?.lat && job.location?.lng;
  
  const timeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000 / 60); // minutes
    
    if (diff < 60) return `${diff}m`;
    if (diff < 1440) return `${Math.floor(diff / 60)}h`;
    return `${Math.floor(diff / 1440)}d`;
  };

  return (
    <div
      data-testid={`job-card-${job.job_id}`}
      onClick={onClick}
      className="job-card p-4 bg-white border border-slate-100 rounded-xl cursor-pointer group animate-fade-in hover:border-slate-200"
      style={{ animationDelay: `${index * 50}ms` }}
    >
      <div className="flex items-start gap-3">
        {/* Category Icon */}
        <div className={`w-12 h-12 ${category.color} rounded-xl flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform relative`}>
          <CategoryIcon className="w-6 h-6 text-white" />
          {/* Map indicator dot */}
          {hasLocation && (
            <div className="absolute -top-1 -right-1 w-4 h-4 bg-emerald-500 rounded-full border-2 border-white flex items-center justify-center">
              <MapPin className="w-2 h-2 text-white" />
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-semibold text-slate-900 line-clamp-1 group-hover:text-emerald-600 transition-colors">
              {job.title}
            </h3>
            {job.urgent && (
              <Badge variant="destructive" className="shrink-0 flex items-center gap-1">
                <Zap className="w-3 h-3" />
                {t('jobs.urgent')}
              </Badge>
            )}
          </div>

          <p className="text-sm text-slate-500 line-clamp-2 mt-1 mb-3">
            {job.description}
          </p>

          {/* Meta info */}
          <div className="flex items-center gap-4 text-sm">
            {/* Budget */}
            <div className="flex items-center gap-1 text-emerald-600 font-semibold">
              <DollarSign className="w-4 h-4" />
              <span>{job.budget}</span>
            </div>

            {/* Location */}
            {job.location?.address && (
              <div className="flex items-center gap-1 text-slate-500">
                <MapPin className="w-4 h-4" />
                <span className="truncate max-w-[120px]">{job.location.address}</span>
              </div>
            )}

            {/* Distance */}
            {job.distance !== undefined && (
              <div className="flex items-center gap-1 text-slate-400">
                <span>{job.distance} {t('jobs.distance')}</span>
              </div>
            )}

            {/* Time */}
            <div className="flex items-center gap-1 text-slate-400 ml-auto">
              <Clock className="w-4 h-4" />
              <span>{timeAgo(job.created_at)}</span>
            </div>
          </div>

          {/* Payment methods */}
          {job.payment_methods?.length > 0 && (
            <div className="flex items-center gap-2 mt-3">
              {job.payment_methods.slice(0, 3).map((method) => (
                <span 
                  key={method}
                  className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-md capitalize"
                >
                  {method}
                </span>
              ))}
              {job.payment_methods.length > 3 && (
                <span className="text-xs text-slate-400">
                  +{job.payment_methods.length - 3}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default JobCard;
